import React, { useEffect, useState } from "react";
import { Mail, Check, LinkIcon, AlertTriangle, Unplug } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";

export default function Settings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try {
      const { data } = await api.get("/oauth/gmail/status");
      setStatus(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  useEffect(() => {
    load();
    // Handle ?gmail=connected / ?gmail=error from OAuth callback redirect
    const g = params.get("gmail");
    if (g === "connected") {
      toast.success("Gmail connected!");
      setParams({}, { replace: true });
    } else if (g === "error") {
      const reason = params.get("reason") || "unknown";
      const detail = params.get("detail");
      toast.error(detail ? `Gmail connect failed: ${reason} — ${detail}` : `Gmail connect failed: ${reason}`, { duration: 12000 });
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/oauth/gmail/login");
      window.location.href = data.auth_url; // full redirect to Google
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to start OAuth");
      setLoading(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Gmail? You won't be able to send emails until you reconnect.")) return;
    try {
      await api.delete("/oauth/gmail/disconnect");
      toast.success("Gmail disconnected");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <Layout title="Settings" breadcrumb={["Settings"]}>
      <div className="max-w-2xl space-y-5">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="settings-gmail-card">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-orange-100 grid place-items-center">
              <Mail size={16} className="text-orange-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Gmail integration</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Connect your Google account to send emails to leads from your own mailbox.
              </p>
            </div>
          </div>

          <div className="p-5">
            {status?.connected ? (
              <div className="space-y-4">
                {status.needs_reconnect && (
                  <div data-testid="settings-gmail-reconnect-required" className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2">
                    <AlertTriangle size={16} className="text-red-600 mt-0.5 shrink-0" />
                    <div className="text-sm text-red-800">
                      <div className="font-semibold">Reconnect required to keep sending</div>
                      <div className="text-xs mt-0.5">
                        This Gmail connection is missing its refresh permission, so sending will fail with an
                        authorization error once the session lapses. Please <b>Disconnect</b> and <b>Connect Gmail</b> again
                        to restore sending.
                      </div>
                    </div>
                  </div>
                )}
                <div data-testid="settings-gmail-connected" className="bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-start gap-2">
                  <Check size={16} className="text-emerald-600 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-semibold text-emerald-900">Connected to {status.connected_email}</div>
                    <div className="text-xs text-emerald-700 mt-0.5">
                      Emails you send from Lead Detail will go from this address and appear in your Sent folder.
                    </div>
                  </div>
                </div>
                <div className="text-xs text-slate-500">
                  <b className="text-slate-700">Granted scopes:</b>{" "}
                  {(status.scopes || []).map((s) => s.replace("https://www.googleapis.com/auth/", "")).join(", ") || "—"}
                </div>
                {!(status.scopes || []).some((s) => s.includes("gmail.readonly")) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2" data-testid="settings-gmail-needs-reconnect">
                    <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
                    <div className="text-xs text-amber-800">
                      <b className="font-semibold">Reconnect required</b> — to auto-sync inbound replies from your Gmail
                      into the lead activity, please disconnect and reconnect so we can request read-only inbox access.
                    </div>
                  </div>
                )}
                <button
                  data-testid="settings-gmail-disconnect"
                  onClick={disconnect}
                  className="px-3 py-2 text-sm rounded-md border border-red-200 bg-white text-red-600 hover:bg-red-50 inline-flex items-center gap-1.5"
                >
                  <Unplug size={13} /> Disconnect Gmail
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2">
                  <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-amber-800">
                    <div className="font-semibold mb-0.5">Gmail not connected</div>
                    <div className="text-xs">
                      You'll be redirected to Google to authorize sending emails from your account. We only request the
                      <code className="px-1 mx-0.5 rounded bg-amber-100 text-amber-900">gmail.send</code> scope — we
                      <b> cannot</b> read your inbox.
                    </div>
                  </div>
                </div>
                <button
                  data-testid="settings-gmail-connect"
                  onClick={connect}
                  disabled={loading}
                  className="px-4 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-medium inline-flex items-center gap-1.5"
                >
                  <LinkIcon size={13} /> {loading ? "Redirecting…" : "Connect Gmail"}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 text-xs text-slate-600 space-y-2" data-testid="settings-help">
          <h3 className="text-sm font-semibold text-slate-900">How it works</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>Click <b>Connect Gmail</b> and pick the Google account you want to send from (e.g. your @losung360.com or @shipmaxx.in mailbox).</li>
            <li>From any Lead Detail page, click <b>Send email</b>, pick an admin-approved template, and send.</li>
            <li>The email is sent via the Gmail API — it lands in your Sent folder and replies come back to your real mailbox.</li>
            <li>Disconnect at any time. You'll need to re-authorize before sending again.</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
}
